import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { useAuth, ProtectedRoute } from '@betstarters/auth'
import {
  AppShell,
  BackofficeHeader,
  BackofficeSidebar,
  LoginPage as KitLoginPage,
  ProfilePage as KitProfilePage,
  VersionFooter,
} from '@betstarters/ui'
import {
  ArrowLeftRight,
  Coins,
  CreditCard,
  Landmark,
  Languages as LanguagesIcon,
  Plug,
  ListChecks,
  Rows3,
  Ruler,
  SlidersHorizontal,
  Dices,
  Gamepad2,
  Globe,
  Layers,
  Map,
  Phone,
  Tags,
  UserCircle,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { api } from './api'
import { BrandsPage } from './pages/Brands'
import { GameTypesPage } from './pages/GameTypes'
import { GameSubTypesPage } from './pages/GameSubTypes'
import { LanguagesPage } from './pages/Languages'
import { GameTypeDetailPage } from './pages/GameTypeDetail'
import { PaymentMethodsPage } from './pages/PaymentMethods'
import { PaymentMethodDetailPage } from './pages/PaymentMethodDetail'
import { CountriesPage } from './pages/Countries'
import { CountryDetailPage } from './pages/CountryDetail'
import { JurisdictionsPage } from './pages/Jurisdictions'
import { JurisdictionDetailPage } from './pages/JurisdictionDetail'
import { PrefixesPage } from './pages/Prefixes'
import { BanksPage } from './pages/Banks'
import { IntegrationsPage } from './pages/Integrations'
import { RuleTemplatesPage } from './pages/RuleTemplates'
import { RuleGroupsPage } from './pages/RuleGroups'
import { RuleSettingsPage } from './pages/RuleSettings'
import { GamesPage } from './pages/Games'
import { RuleThresholdsPage } from './pages/RuleThresholds'
import { LoadErrorBanner } from './components/loadErrorBanner'
import { RuleSettingDetailPage } from './pages/RuleSettingDetail'
import { BankDetailPage } from './pages/BankDetail'
import { CurrenciesPage } from './pages/Currencies'
import { CurrencyDetailPage } from './pages/CurrencyDetail'
import { CausalsPage } from './pages/Causals'
import { CausalDetailPage } from './pages/CausalDetail'

/**
 * bs-connekto backoffice shell.
 *
 * Authentication, login and profile come from the shared kit
 * (@betstarters/auth + @betstarters/ui) against the canonical /api/v1/auth/*
 * contract the Go service exposes. Only the sidebar sections are product-specific.
 */

function ConnektoLoginPage() {
  const navigate = useNavigate()
  const { refreshUser } = useAuth()
  return (
    <KitLoginPage
      subtitle="BetStarters Connekto"
      api={api}
      onSuccess={async () => {
        await refreshUser()
        navigate('/')
      }}
    />
  )
}

function ConnektoLayout({ children }: { children: React.ReactNode }) {
  const { user, logout } = useAuth()
  const navigate = useNavigate()
  const { t } = useTranslation('app')

  const sections = [
    {
      // Brands here are game studios/providers (Amatic, Evolution, …), not
      // bookmaker brands — the whole section is the Games family.
      label: t('nav.games'),
      items: [
        { to: '/brands', icon: Tags, label: t('nav.brands') },
        { to: '/games', icon: Gamepad2, label: t('nav.gamesCatalogue') },
        { to: '/game-types', icon: Dices, label: t('nav.gameTypes') },
        { to: '/game-subtypes', icon: Layers, label: t('nav.gameSubTypes') },
      ],
    },
    {
      label: t('nav.psp'),
      items: [
        { to: '/payment-methods', icon: CreditCard, label: t('nav.paymentMethods') },
        { to: '/banks', icon: Landmark, label: t('nav.banks') },
      ],
    },
    {
      label: t('nav.geo'),
      items: [
        { to: '/countries', icon: Globe, label: t('nav.countries') },
        { to: '/jurisdictions', icon: Map, label: t('nav.jurisdictions') },
        { to: '/prefixes', icon: Phone, label: t('nav.prefixes') },
      ],
    },
    {
      label: t('nav.finance'),
      items: [
        { to: '/currencies', icon: Coins, label: t('nav.currencies') },
        { to: '/causals', icon: ArrowLeftRight, label: t('nav.causals') },
      ],
    },
    {
      label: t('nav.platform'),
      items: [
        { to: '/integrations', icon: Plug, label: t('nav.integrations') },
        { to: '/languages', icon: LanguagesIcon, label: t('nav.languages') },
      ],
    },
    {
      // Its own heading rather than three more items under Platform: the Setting
      // schema is a tree of its own, and the pages that follow (groups,
      // settings, thresholds) belong under this label.
      label: t('nav.rulesEngine'),
      items: [
        { to: '/rules/templates', icon: SlidersHorizontal, label: t('nav.ruleTemplates') },
        { to: '/rules/groups', icon: Rows3, label: t('nav.ruleGroups') },
        { to: '/rules/settings', icon: ListChecks, label: t('nav.ruleSettings') },
        { to: '/rules/thresholds', icon: Ruler, label: t('nav.ruleThresholds') },
      ],
    },
    {
      label: t('nav.account'),
      items: [{ to: '/profile', icon: UserCircle, label: t('nav.profile') }],
    },
  ]

  const sidebar = <BackofficeSidebar subtitle="Connekto" sections={sections} responsive />

  const header = (
    <BackofficeHeader
      userEmail={user?.email}
      userRole={(user as { platform_role?: string } | null)?.platform_role}
      onProfileClick={() => navigate('/profile')}
      onLogout={() => {
        logout()
        navigate('/login')
      }}
      showLanguageSwitcher
    />
  )

  return (
    <AppShell sidebar={sidebar} header={header} footer={<VersionFooter />}>
      {/* Above the page, because it explains why the page below is empty. */}
      <LoadErrorBanner />
      {children}
    </AppShell>
  )
}

function ConnektoProfilePage() {
  const { user, refreshUser } = useAuth()
  if (!user) return null
  return <KitProfilePage user={user} api={api} onProfileUpdated={refreshUser} />
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<ConnektoLoginPage />} />
      {/* The catalogue is the landing page; more catalogues get their own route. */}
      <Route path="/" element={<Navigate to="/brands" replace />} />
      <Route
        path="/brands"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <BrandsPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/game-types"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <GameTypesPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/game-types/:id"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <GameTypeDetailPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/game-subtypes"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <GameSubTypesPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/languages"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <LanguagesPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-methods"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <PaymentMethodsPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment-methods/:id"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <PaymentMethodDetailPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/banks"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <BanksPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/banks/:id"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <BankDetailPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/countries"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <CountriesPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/countries/:id"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <CountryDetailPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/jurisdictions"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <JurisdictionsPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/jurisdictions/:id"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <JurisdictionDetailPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/prefixes"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <PrefixesPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/currencies"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <CurrenciesPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/currencies/:id"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <CurrencyDetailPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/causals"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <CausalsPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/causals/:id"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <CausalDetailPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/integrations"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <IntegrationsPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/games"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <GamesPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rules/templates"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <RuleTemplatesPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rules/groups"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <RuleGroupsPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rules/settings"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <RuleSettingsPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rules/thresholds"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <RuleThresholdsPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/rules/settings/:id"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <RuleSettingDetailPage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute fallback={<Navigate to="/login" />}>
            <ConnektoLayout>
              <ConnektoProfilePage />
            </ConnektoLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  )
}
